import { AllegatoDtoTipoDocumentoEnum, PagamentoDto, SoggettoDto } from "@api/client";
import { fileUpload } from "common/_redux/file/actions";
import { paymentsGetPayments, paymentsUpdatePayments } from "common/_redux/payments/actions";
import { paymentsPaymentsSelector } from "common/_redux/payments/selectors";
import { serviceServiceSelector } from "common/_redux/service/selectors";
import { subjectGetDetails, subjectSetTitolariEffettiviAdded } from "common/_redux/subject/actions";
import { titolariEffettiviAddedSelector } from "common/_redux/subject/selectors";
import BftAccordion from "common/components/accordion";
import BftButton from "common/components/button";
import BftCodeInput from "common/components/code-input";
import DettaglioPianoCard from "common/components/dettaglio-piano-card";
import BftDropzone from "common/components/dropzone";
import BftInfoTooltip from "common/components/info-tooltip";
import BftInput from "common/components/input";
import RiepilogoTitolariCard from "common/components/riepilogo-titolari";
import { FilesUtils } from "common/file.utils";
import { paymentsSchema } from "common/validators/validators";
import { IdTipoDocumenti, IdTipoDocumentiEstesi } from "constants/global";
import { AppRouterService } from "core/routes/app-router.service";
import { PathEnum } from "core/routes/path.enum";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import { Formik, FormikProps } from "formik";
import Footer from "layout/components/footer";
import Header from "layout/components/header";
import Sidebar from "layout/components/sidebar";
import Topbar from "layout/components/topbar";
import { FileDocument } from "models/files";
import { useEffect, useRef, useState } from "react";
import { Col, FloatingLabel, Form, Row } from "react-bootstrap";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";

const title = "5. Pagamenti";
const subtitle =
	"Completa le informazioni richieste e controlla che quelle già inserite siano corrette.";
const messaggioPartitaIva = "Partita IVA";
const messaggioIban = "IBAN su cui effettuare l'addebito";
const messaggioCodiceFiscale = "Codice Fiscale";
const messaggioPreponente =
	"Dati del Legale Rappresentante che autorizza l’addebito dei costi di servizio";
const datiPagamento = "Tipologia di pagamento";

export interface ContrattoExt extends PagamentoDto {
	INT_ESTRATTO_CONTO?: FileDocument | undefined;
}

const FifthStepPage: React.FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const formRef = useRef<FormikProps<PagamentoDto>>(null);
	const service = useAppSelector(serviceServiceSelector);
	const payments = useAppSelector(paymentsPaymentsSelector);
	const titolariEffettiviStored = useAppSelector(titolariEffettiviAddedSelector);

	const [iban, setIBAN] = useState("");
	const [showDropzone, setShowDropzone] = useState(false);
	const [showErrorText, setShowErrorText] = useState(false);

	const navBack = () => {
		if (service?.convenzioniPos?.items?.some((e) => e.name == null)){
			navigate(AppRouterService.get(PathEnum.step3));
		}else {
			navigate(-1);
		}
	};

	const _handleSubmit = async (values: ContrattoExt) => {
		let _values = { ...values };
		let documents: (FileDocument | undefined)[] = [];
		if (values?.INT_ESTRATTO_CONTO) documents = [_values.INT_ESTRATTO_CONTO];
		documents = documents?.filter((f) => f != undefined && f?.size > 0);
		delete _values?.INT_ESTRATTO_CONTO;

		dispatch(fileUpload(documents as FileDocument[]))
			.unwrap()
			.then((res) => {
				if (res || documents.length < 1) {
					dispatch(paymentsUpdatePayments(values))
						.unwrap()
						.then((res) => {
							if (res) {
								navigate(AppRouterService.get(PathEnum.step6));
							}
						})
						.catch((error) => {
							if (error.message === "500") {
								setShowDropzone(false);
								setShowErrorText(true);
							}
							if (error.message === "501") {
								setShowErrorText(false);
								setShowDropzone(true);
							}
						});
				}
			});
	};

	useEffect(() => {
		dispatch(paymentsGetPayments());
	}, []);

	useEffect(() => {
		formRef?.current?.resetForm();
	}, [payments]);

	useEffect(() => {
		if (!titolariEffettiviStored) getSubjects();
	}, [titolariEffettiviStored]);

	const getSubjects = () => {
		dispatch(subjectGetDetails())
			.unwrap()
			.then((subjectRes) => {
				if (subjectRes?.elencoTitolariEffettivi?.length) {
					const esponentiMapped = subjectRes.elencoTitolariEffettivi?.map((item: SoggettoDto) => ({
						...item,
						idFE: item.id,
						fulfilled: item.fullfiled,
					}));
					dispatch(
						subjectSetTitolariEffettiviAdded({
							list: esponentiMapped,
						})
					);
				}
			});
	};

	const handleInputChange = (text: string | undefined) => {
		if (!text) return;

		const cleanedInput = text.replace(/ /g, "");
		let formattedIBAN = "";
		for (let i = 0; i < cleanedInput.length; i++) {
			formattedIBAN += cleanedInput[i];
			if (i === 1 || i === 3 || i === 4 || i === 9 || i === 14) {
				formattedIBAN += " ";
			}
		}

		return formattedIBAN;
	};

	return (
		<>
			<Sidebar />
			<div className="wrapper d-flex flex-column min-vh-100">
				<div className="body flex-grow-1 px-0">
					<Header />
					<Topbar />
					<div className="container-fluid body-section px-0">
						<section>
							<Row>
								<Col sm={12} lg={8}>
									<Formik
										innerRef={formRef}
										initialValues={
											{
												reagioneSociale: payments?.reagioneSociale || "",
												partitaIva: payments?.partitaIva || undefined,
												codiceFiscale: payments?.codiceFiscale || undefined,
												iban: payments?.iban || iban || "",
												nomeProponente: payments?.nomeProponente || "",
												cognomeProponente: payments?.cognomeProponente || "",
												codiceFiscaleProponente: payments?.codiceFiscaleProponente || "",
												tipologiaPagamento: payments?.tipologiaPagamento,
												idPagamento: payments?.idPagamento,
												INT_ESTRATTO_CONTO: FilesUtils.AllegatoDtoToFileDocument(
													payments?.elencoAllegati?.find(
														(f) => f.tipoDocumento == AllegatoDtoTipoDocumentoEnum.IntEstrattoConto
													)
												),
											} as ContrattoExt
										}
										onSubmit={_handleSubmit}
										validationSchema={paymentsSchema}
										enableReinitialize
									>
										{({
											values,
											isValid,
											errors,
											touched,
											handleSubmit,
											handleChange,
											handleBlur,
											setFieldValue,
											setFieldTouched,
										}) => {
											const handleFileChange = (name: string, file?: FileDocument | null) => {
												setFieldTouched(name);
												setFieldValue(name, file);
											};

											return (
												<form onSubmit={handleSubmit}>
													<h2 className="mb-3 ms-3">{title}</h2>
													<span className="gray">{subtitle}</span>

													<BftAccordion
														title="Mandato di addebito diretto SDD e/o di accredito"
														subtitle="Autorizzazione di addebito in qualità di Debitore degli importi spettanti alla Società (Buffetti Finance S.p.A.) a fronte dei Servizi fruiti e/o di accredito delle transazioni incassate a mezzo POS."
														index={1}
														total={1}
														isActive={true}
													>
														<div className="px-1">
															<h4 className="container mb-3 mt-3">Dati del debitore</h4>
															<div>
																<BftInput
																	type="text"
																	name="reagioneSociale"
																	placeholder="Denominazione/Ragione sociale*"
																	value={values.reagioneSociale?.toUpperCase()}
																	onChange={handleChange}
																	onBlur={handleBlur}
																	isInvalid={!!(errors.reagioneSociale && touched.reagioneSociale)}
																/>
																{errors.reagioneSociale && touched.reagioneSociale && (
																	<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
																		{errors.reagioneSociale}
																	</Form.Control.Feedback>
																)}
															</div>
															<div>
																{values.partitaIva ? (
																	<div className="d-flex align-items-center">
																		<BftCodeInput
																			length={11}
																			id="partitaIva"
																			key="partitaIva"
																			pattern="^([0-9]{1,})$"
																			placeholder="Partita IVA*"
																			inputCode={values?.partitaIva}
																			codeHandler={handleChange("partitaIva")}
																			isInvalid={!!(errors.partitaIva && touched.partitaIva)}
																			disabled
																		/>
																		<BftInfoTooltip
																			message={messaggioPartitaIva}
																			className="mx-lg-3 mx-0 mb-2"
																		/>
																	</div>
																) : (
																	<div className="d-flex align-items-center">
																		<BftCodeInput
																			length={16}
																			id="codiceFiscale"
																			key={"codiceFiscale"}
																			pattern={"^([A-Za-z0-9]{1,})$"}
																			placeholder={"Codice Fiscale*"}
																			codeHandler={handleChange("codiceFiscale")}
																			inputCode={values?.codiceFiscale}
																			disabled
																		/>
																		<BftInfoTooltip
																			message={messaggioCodiceFiscale}
																			className="mx-lg-3 mx-0 mb-2"
																		/>
																	</div>
																)}
															</div>
															<div>
																<div className="d-flex align-items-center mt-2">
																	<div className="container">
																		<FloatingLabel
																			label="Iban su cui effettuare l'addebito*"
																			className="w-100 mb-3"
																		>
																			<InputMask
																				mask={"aa 99 * 99999 99999 ************"}
																				maskChar={" "}
																				name="iban"
																				value={values.iban?.toUpperCase()}
																				onChange={(e) => {
																					let event = {
																						...e,
																						currentTarget: {
																							...e.currentTarget,
																							value: e.currentTarget.value.replace(/ /g, ""),
																						},
																						target: {
																							...e.target,
																							value: e.target.value.replace(/ /g, ""),
																						},
																					};
																					handleChange("iban")(event);
																				}}
																				onBlur={handleBlur}
																				className={`form-control ${
																					!!(errors.iban && touched.iban) ? "is-invalid" : ""
																				}`}
																				// isInvalid={!!(errors.iban && touched.iban)}
																			/>
																		</FloatingLabel>
																	</div>

																	<BftInfoTooltip
																		message={messaggioIban}
																		className="mx-lg-3 mx-0 mb-2"
																	/>
																</div>

																{errors.iban && touched.iban && (
																	<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
																		{errors.iban}
																	</Form.Control.Feedback>
																)}
															</div>
															{showErrorText && (
																<div>
																	<h6 className="text-danger container my-2">
																		Attenzione! L'indirizzo IBAN del CC non risulta collegato alla
																		Piva della tua società. Verifica la correttezza e modifica i
																		campi errati.
																	</h6>
																</div>
															)}
															{showDropzone && (
																<div className="">
																	<h6
																		className="d-flex container d-flex justify-content-center text-danger container my-2"
																		style={{ fontSize: "18px" }}
																	>
																		Al fine di verificare la correttezza dell'iban sopra indicato,
																		necessitiamo di una copia del frontespizio del tuo estratto
																		conto.
																	</h6>
																	<div className="d-flex container d-flex justify-content-center">
																		<h6 className="text-danger" style={{ fontSize: "18px" }}>
																			Frontespizio di Estratto Conto*
																		</h6>
																	</div>
																	<Col
																		md={6}
																		className="my-3 d-flex container d-flex justify-content-center flex-column"
																	>
																		<BftDropzone
																			label=""
																			idTipoDocumento={IdTipoDocumenti.INT_ESTRATTO_CONTO}
																			trascinaDocumento={IdTipoDocumentiEstesi?.INT_ESTRATTO_CONTO}

																			file={values.INT_ESTRATTO_CONTO}
																			onChange={(e) =>
																				handleFileChange(IdTipoDocumenti.INT_ESTRATTO_CONTO, e)
																			}
																			isInvalid={
																				!!(touched.INT_ESTRATTO_CONTO && errors.INT_ESTRATTO_CONTO)
																			}
																		/>
																	</Col>
																</div>
															)}
															<div className="container d-flex align-items-center mt-4 mb-2">
																<h4 className="me-3">Dati del preponente</h4>
																<BftInfoTooltip message={messaggioPreponente} className="mb-2" />
															</div>
															<Row>
																<Col sm={12} md={6}>
																	<BftInput
																		type={"text"}
																		name={"nomeProponente"}
																		placeholder={"Nome*"}
																		value={values.nomeProponente?.toUpperCase()}
																		onChange={handleChange}
																		onBlur={handleBlur}
																		isInvalid={!!(errors.nomeProponente && touched.nomeProponente)}
																	/>
																	{errors.nomeProponente && touched.nomeProponente && (
																		<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
																			{errors.nomeProponente}
																		</Form.Control.Feedback>
																	)}
																</Col>

																<Col sm={12} md={6}>
																	<BftInput
																		type={"text"}
																		name={"cognomeProponente"}
																		placeholder={"Cognome*"}
																		value={values.cognomeProponente?.toUpperCase()}
																		onChange={handleChange}
																		onBlur={handleBlur}
																		isInvalid={
																			!!(errors.cognomeProponente && touched.cognomeProponente)
																		}
																	/>
																	{errors.cognomeProponente && touched.cognomeProponente && (
																		<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
																			{errors.cognomeProponente}
																		</Form.Control.Feedback>
																	)}
																</Col>
															</Row>
															<BftCodeInput
																length={16}
																id="codiceFiscale"
																key={"codiceFiscale"}
																pattern={"^([A-Za-z0-9]{1,})$"}
																placeholder={"Codice Fiscale*"}
																capitalize={true}
																inputCode={values?.codiceFiscaleProponente}
																codeHandler={handleChange("codiceFiscaleProponente")}
																isInvalid={
																	!!(
																		errors.codiceFiscaleProponente &&
																		touched.codiceFiscaleProponente
																	)
																}
																disabled
															/>
															{errors.codiceFiscaleProponente &&
																touched.codiceFiscaleProponente && (
																	<Form.Control.Feedback className="ms-3 mt-0" type="invalid">
																		{errors.codiceFiscaleProponente}
																	</Form.Control.Feedback>
																)}
															<div className=" container d-flex justify-content-start align-items-center mt-5 mb-3">
																<h5 className="me-3">Tipologia di pagamento</h5>
																<BftInfoTooltip message={datiPagamento} className="mb-2" />
															</div>
															<div className="container">
																<BftButton
																	isDisabled={true}
																	className="container text-left p-4 border border-dark"
																>
																	Mandato di tipo ricorrente
																</BftButton>
															</div>
														</div>
													</BftAccordion>

													<Row className="justify-content-between mx-1">
														<Col xs={1}>
															<BftButton
																className="btn-buffetti-secondary px-3"
																onClick={() => navBack()}
															>
																<i className="fas fa-arrow-left"></i>
															</BftButton>
														</Col>

														<Col xs={10} md={11}>
															<BftButton
																type="submit"
																className="btn-buffetti col-12"
																isDisabled={!isValid}
															>
																Vai allo step successivo
															</BftButton>
														</Col>
													</Row>
												</form>
											);
										}}
									</Formik>
								</Col>
								<Col sm={12} md={4} className="side-card">
									<DettaglioPianoCard />
									<RiepilogoTitolariCard />
								</Col>
							</Row>
						</section>
					</div>
				</div>
				<Footer />
			</div>
		</>
	);
};

export default FifthStepPage;
